import { useState } from "react";
import { TableColumn } from "./TableContainer";
import TableDropdown, { TableDropdownOption } from "./TableDropdown";
import useModal from "../../utils/hooks/useModal";
import { ModalType } from "../../utils/store/reducer/slices/modalReducer";

/**
 * Provides a layer ontop of the regular table dropdown
 * that only fires the onChange handler after the user
 * has finished typing - useful for minimising API
 * calls when used for searching options.
 */
const useTableDropdown = <T extends any>({
  name,
  options,
  columns,
  placeholder,
  className,
  onSearch,
  onSubmit
}: {
  name: string;
  options: TableDropdownOption<T>[];
  columns: TableColumn[];
  placeholder?: string;
  className?: string;
  onSearch: (term: string) => void;
  onSubmit: (value: T) => void;
}) => {
  const { modal } = useModal(ModalType.DROPDOWN, name);
  const [value, setValue] = useState("");
  const [timeout, setTimeoutState] = useState<NodeJS.Timeout>();

  const onChange = (value: string) => {
    setValue(value);
    clearTimeout(Number(timeout));

    const newTimeout = setTimeout(() => onSearch(value), 300);
    setTimeoutState(newTimeout);
  };

  const dropdown = (
    <TableDropdown
      name={name}
      value={value}
      onChange={onChange}
      options={options}
      columns={columns}
      className={className}
      placeholder={placeholder}
      onSubmit={value => {
        onSubmit(value);
        setValue("");
      }}
    />
  );

  return {
    dropdown,
    focused: modal,
    setValue
  };
};

export default useTableDropdown;
